import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Zap,
  GitBranch,
  Wrench,
  Server,
  Play,
  Calendar,
  Webhook,
  Phone,
  MessageSquare,
  Users,
  FileText,
  Tag,
  ArrowRight,
  Split,
  RotateCcw,
  Clock,
  AlertTriangle,
  Variable,
  Globe,
  Mail,
  Bell,
  Shuffle,
  FileCode,
  Bot,
  Brain,
  Layers,
  Send,
  Contact,
  PhoneCall,
  Database,
  Video,
  Smartphone,
  MessageCircle,
  CheckCircle,
  PhoneOff,
  UserPlus,
  Film,
  UserCheck,
  UserX,
  Package,
  PackagePlus,
  HardHat,
  LogIn,
  LogOut,
  Award,
  FilePlus,
  ClipboardEdit,
  ArrowRightLeft,
  RefreshCw,
} from 'lucide-react';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@easyflow/ui-frontend/components/ui/accordion';

interface NodeDefinition {
  id: string;
  display_name: string;
  description: string;
  category: string;
  icon: string;
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
  mcp_server_id?: string;
  mcp_server_name?: string;
}

interface NodePanelProps {
  availableNodes: Record<string, NodeDefinition[]>;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  triggers: { label: 'Triggers', icon: Zap, color: 'text-cyan-400' },
  flow_control: { label: 'Controllo Flusso', icon: GitBranch, color: 'text-purple-400' },
  utilities: { label: 'Utilità', icon: Wrench, color: 'text-green-400' },
  video_call: { label: 'Video Call', icon: Video, color: 'text-indigo-400' },
  messaging: { label: 'Messaggistica', icon: Send, color: 'text-blue-400' },
  crm: { label: 'CRM', icon: Contact, color: 'text-orange-400' },
  ai: { label: 'Intelligenza Artificiale', icon: Brain, color: 'text-violet-400' },
  supplychain: { label: 'Supply Chain & Catalogo', icon: Package, color: 'text-emerald-400' },
  hr: { label: 'Risorse Umane', icon: HardHat, color: 'text-rose-400' },
};

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  play: Play,
  calendar: Calendar,
  webhook: Webhook,
  phone: Phone,
  'phone-incoming': Phone,
  'phone-missed': Phone,
  voicemail: MessageSquare,
  'message-square': MessageSquare,
  users: Users,
  'file-text': FileText,
  tag: Tag,
  'git-branch': GitBranch,
  split: Split,
  'rotate-ccw': RotateCcw,
  clock: Clock,
  'alert-triangle': AlertTriangle,
  variable: Variable,
  'arrow-right': ArrowRight,
  globe: Globe,
  mail: Mail,
  bell: Bell,
  shuffle: Shuffle,
  'file-code': FileCode,
  bot: Bot,
  brain: Brain,
  layers: Layers,
  zap: Zap,
  server: Server,
  wrench: Wrench,
  send: Send,
  contact: Contact,
  'phone-call': PhoneCall,
  database: Database,
  video: Video,
  link: Globe,
  smartphone: Smartphone,
  'message-circle': MessageCircle,
  'check-circle': CheckCircle,
  'phone-off': PhoneOff,
  'user-plus': UserPlus,
  film: Film,
  'user-check': UserCheck,
  'user-x': UserX,
  package: Package,
  'package-plus': PackagePlus,
  'hard-hat': HardHat,
  'log-in': LogIn,
  'log-out': LogOut,
  award: Award,
  'file-plus': FilePlus,
  'clipboard-edit': ClipboardEdit,
  'arrow-right-left': ArrowRightLeft,
  'refresh-cw': RefreshCw,
};

type DraggableNodeVariant =
  | { kind: 'static'; category: string }
  | { kind: 'mcp' };

interface DraggableNodeItemProps {
  node: NodeDefinition;
  variant: DraggableNodeVariant;
  onDragStart: (e: React.DragEvent, node: NodeDefinition) => void;
  getIcon: (iconName: string) => React.ComponentType<{ className?: string }>;
}

function DraggableNodeItem({ node, variant, onDragStart, getIcon }: DraggableNodeItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const updatePosition = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ left: r.right + 8, top: r.top + r.height / 2 });
  };

  const handleEnter = () => updatePosition();
  const handleLeave = () => setPos(null);

  useEffect(() => {
    if (!pos) return;
    const onScroll = () => setPos(null);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [pos]);

  const NodeIcon = getIcon(node.icon);

  const isMcp = variant.kind === 'mcp';
  const containerClass = isMcp
    ? 'flex items-center gap-2 p-1.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 cursor-grab active:cursor-grabbing transition-colors'
    : 'flex items-center gap-2 p-2 rounded-md bg-muted/50 hover:bg-muted cursor-grab active:cursor-grabbing transition-colors';
  const iconWrapperClass = isMcp
    ? 'p-1 rounded bg-amber-600'
    : `p-1.5 rounded ${getCategoryBgColor(variant.kind === 'static' ? variant.category : '')}`;
  const iconSizeClass = isMcp ? 'h-3 w-3 text-primary-foreground' : 'h-3.5 w-3.5 text-primary-foreground';
  const labelClass = isMcp
    ? 'text-xs text-muted-foreground truncate'
    : 'text-sm text-foreground truncate';

  return (
    <>
      <div
        ref={ref}
        draggable
        onDragStart={(e) => {
          setPos(null);
          onDragStart(e, node);
        }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={containerClass}
      >
        <div className={iconWrapperClass}>
          <NodeIcon className={iconSizeClass} />
        </div>
        <span className={labelClass}>{node.display_name}</span>
      </div>
      {pos && createPortal(
        <div
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: 'translateY(-50%)',
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth: '20rem',
          }}
          className="bg-popover text-popover-foreground border rounded-md shadow-md px-3 py-2"
        >
          <p className="font-medium text-sm">{node.display_name}</p>
          <p className="text-xs text-muted-foreground mt-1 whitespace-normal">{node.description}</p>
          {isMcp && (
            <Badge className="mt-2 bg-amber-600 text-primary-foreground text-xs">
              MCP: {node.mcp_server_name}
            </Badge>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export function NodePanel({ availableNodes }: NodePanelProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const onDragStart = (event: React.DragEvent, node: NodeDefinition) => {
    console.log('[NodePanel] dragstart node=', node.id, 'category=', node.category);
    event.dataTransfer.setData('application/reactflow', JSON.stringify(node));
    event.dataTransfer.effectAllowed = 'move';
  };

  const getIcon = (iconName: string) => {
    const Icon = ICON_MAP[iconName] || Zap;
    return Icon;
  };

  const filterNodes = (nodes: NodeDefinition[]) => {
    if (!searchQuery) return nodes;
    const query = searchQuery.toLowerCase();
    return nodes.filter(
      (node) =>
        node.display_name.toLowerCase().includes(query) ||
        node.description.toLowerCase().includes(query) ||
        node.id.toLowerCase().includes(query)
    );
  };

  const staticCategories = ['triggers', 'flow_control', 'messaging', 'video_call', 'crm', 'ai', 'utilities', 'supplychain', 'hr'];
  const mcpCategories = Object.keys(availableNodes).filter((cat) => cat.startsWith('mcp:'));

  return (
    <div className="w-72 border-r bg-card/50 backdrop-blur flex flex-col">
      <div className="p-4 border-b">
        <h3 className="text-sm font-semibold text-foreground mb-3">Nodi Disponibili</h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca nodi..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 text-sm"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <Accordion type="multiple" defaultValue={[]} className="px-2">
          {staticCategories.map((category) => {
            const config = CATEGORY_CONFIG[category] || { label: category, icon: Zap, color: 'text-muted-foreground' };
            const CategoryIcon = config.icon;
            const nodes = filterNodes(availableNodes[category] || []);

            if (nodes.length === 0 && searchQuery) return null;

            return (
              <AccordionItem key={category} value={category} className="border-border">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className={`h-4 w-4 ${config.color}`} />
                    <span className="text-sm font-medium text-foreground">{config.label}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {nodes.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-2">
                  <div className="space-y-1">
                    {nodes.map((node) => (
                      <DraggableNodeItem
                        key={node.id}
                        node={node}
                        variant={{ kind: 'static', category }}
                        onDragStart={onDragStart}
                        getIcon={getIcon}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}

          {mcpCategories.length > 0 && (
            <AccordionItem value="mcp-servers" className="border-border">
              <AccordionTrigger className="py-2 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Server className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-medium text-foreground">Server MCP</span>
                  <Badge variant="secondary" className="ml-auto bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs">
                    {mcpCategories.length}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-2">
                <Accordion type="multiple" className="pl-2">
                  {mcpCategories.map((category) => {
                    const serverName = category.replace('mcp:', '');
                    const nodes = filterNodes(availableNodes[category] || []);

                    if (nodes.length === 0 && searchQuery) return null;

                    // Task #1829 — visual identity per-server: cerca metadata
                    // nei nodi (icon_url/accent_color iniettati dal backend
                    // discovery). Fallback ai colori amber storici.
                    const meta = (nodes[0] as { server_icon_url?: string; server_accent_color?: string } | undefined);
                    const iconUrl = meta?.server_icon_url ?? null;
                    const accent = meta?.server_accent_color ?? null;

                    return (
                      <AccordionItem key={category} value={category} className="border-border/50">
                        <AccordionTrigger className="py-1.5 hover:no-underline">
                          <div className="flex items-center gap-2">
                            {iconUrl
                              ? <img src={iconUrl} alt="" className="h-3.5 w-3.5 rounded-sm" />
                              : <Server className="h-3.5 w-3.5" style={{ color: accent ?? undefined }} />
                            }
                            <span className="text-xs font-medium text-muted-foreground">{serverName}</span>
                            <Badge variant="secondary" className="ml-auto text-xs" style={accent ? { backgroundColor: `${accent}20`, color: accent } : undefined}>
                              {nodes.length}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-1">
                          <div className="space-y-1">
                            {nodes.map((node) => (
                              <DraggableNodeItem
                                key={node.id}
                                node={node}
                                variant={{ kind: 'mcp' }}
                                onDragStart={onDragStart}
                                getIcon={getIcon}
                              />
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </ScrollArea>
    </div>
  );
}

function getCategoryBgColor(category: string): string {
  switch (category) {
    case 'triggers':
      return 'bg-cyan-600';
    case 'flow_control':
      return 'bg-purple-600';
    case 'utilities':
      return 'bg-green-600';
    case 'messaging':
      return 'bg-blue-600';
    case 'video_call':
      return 'bg-indigo-600';
    case 'crm':
      return 'bg-orange-600';
    case 'ai':
      return 'bg-violet-600';
    case 'supplychain':
      return 'bg-emerald-600';
    case 'hr':
      return 'bg-rose-600';
    default:
      return 'bg-muted';
  }
}
